import { useState, useEffect, useCallback, useMemo } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type { AuthState } from "@/services/authService";
import { fetchCurrentUser, fetchChatThreads, type ThreadListItem } from "@/services/api";
import { logoutAll } from "@/services/adminCustomerAuth";
import { analytics, trackLogout } from "@/analytics/analytics";
import AppLayout from "@/components/AppLayout";
import ChatHeader from "@/components/ChatHeader";
import DevModeIndicator from "@/components/DevModeIndicator";
import RootLogin from "@/pages/RootLogin";
import OnboardingModal from "@/components/OnboardingModal";
import { buildOnboardingProfile } from "@/components/onboarding/profileDefaults";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import SubscriptionGateOverlay, {
  type BlockedSubscriptionState,
} from "@/components/SubscriptionGateOverlay";
import { getSubscription } from "@/services/payments";
import { updateProfileField } from "@/services/profile";
import { clearNextStepCache } from "@/services/nextStep";
import {
  EndUserContext,
  type EndUserContextValue,
  type PlayerSummaryData,
  type ChatHistoryItem,
  defaultSummary,
} from "@/contexts/EndUserContext";

const DEV_MODE: boolean = import.meta.env.VITE_DEV_MODE === "true" || import.meta.env.DEV === true;

const EndUserLayout = () => {
  const navigate = useNavigate();
  // ─── Auth state ───
  const [authState, setAuthState] = useState<AuthState | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // ─── Player summary ───
  const [playerSummary, setPlayerSummary] = useState<PlayerSummaryData>(defaultSummary);
  const updatePlayerSummary = useCallback((partial: Partial<PlayerSummaryData>) => {
    setPlayerSummary(prev => ({ ...prev, ...partial }));
  }, []);

  // ─── User type ───
  const [userType, setUserType] = useState<"player" | "coach">("player");

  // ─── Sidebar ───
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const openSidebar = useCallback(() => setIsSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

  // ─── Chat history ───
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([]);

  // ─── Header callbacks (wrapped in object so useState doesn't invoke them) ───
  const [headerCallbacks, setHeaderCallbacks] = useState<{
    onShare?: () => void;
  }>({});
  const setOnShare = useCallback((fn: (() => void) | undefined) => {
    setHeaderCallbacks(prev => ({ ...prev, onShare: fn }));
  }, []);

  // ─── Raw /users/me data (so child pages don't need to re-fetch) ───
  const [currentUser, setCurrentUser] = useState<any | null>(null);

  // ─── Subscription gating (Individual Signup users) ───
  const [subscriptionState, setSubscriptionState] = useState<string | null>(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState<string | null>(null);
  const setSubscriptionGate = useCallback((state: string) => {
    setSubscriptionState(state);
  }, []);

  // ─── Re-init key: bump to re-run auth init (e.g. after login) ───
  const [reinitKey, setReinitKey] = useState(0);

  // ─── Sidebar next step dismissals (server-persisted on the profile) ───
  const [dismissedNextSteps, setDismissedNextSteps] = useState<string[]>([]);

  // ─── Extract player summary from /users/me response ───
  const extractSummaryFromUser = useCallback(
    (me: any) => {
      const meUserType: "player" | "coach" = (me as any)?.userType || "player";
      setUserType(meUserType);

      if (meUserType === "coach") {
        const profiles = (me as any)?.linkedProfiles || [];
        const isLinked = profiles.length > 0;

        let savedProfile: string | null = null;
        try {
          savedProfile = sessionStorage.getItem("recruit_ai_coach_active_profile");
        } catch {}
        const hasValidSaved =
          savedProfile && profiles.some((p: any) => p.jgsProfileId === savedProfile);
        const activeProfile = hasValidSaved
          ? profiles.find((p: any) => p.jgsProfileId === savedProfile)
          : profiles[0] || null;

        updatePlayerSummary({
          playerName:
            activeProfile?.displayName || activeProfile?.jgsData?.player_name || "Player Name",
          jgsRank:
            typeof activeProfile?.jgsData?.rank === "number"
              ? String(activeProfile.jgsData.rank)
              : "N/A",
          classRank:
            typeof activeProfile?.jgsData?.class_rank === "number"
              ? String(activeProfile.jgsData.class_rank)
              : "N/A",
          classYear: activeProfile?.jgsData?.class_year
            ? String(activeProfile.jgsData.class_year)
            : "N/A",
          scoringDifferential:
            typeof activeProfile?.jgsData?.scoring_differential === "number"
              ? activeProfile.jgsData.scoring_differential.toFixed(2)
              : activeProfile?.jgsData?.scoring_differential || "N/A",
          isLinked,
        });

        setAuthState(prev => (prev ? { ...prev, hasJgsMapping: isLinked } : prev));
      } else {
        const info = me?.jgs_student_data || null;
        const profile = (me as any)?.profile || null;
        const displayName = profile?.displayName ? String(profile.displayName).trim() : "";

        const has =
          typeof (me as any)?.has_jgs_mapping !== "undefined"
            ? !!(me as any).has_jgs_mapping
            : !!(me as any)?.hasJgsMapping;

        updatePlayerSummary({
          playerName: displayName || info?.player_name || "Player Name",
          jgsRank: typeof info?.rank === "number" ? String(info.rank) : "N/A",
          classRank: typeof info?.class_rank === "number" ? String(info.class_rank) : "N/A",
          classYear: info?.class_year ? String(info.class_year) : "N/A",
          profileGradYear: profile?.gradYear ? String(profile.gradYear) : "",
          scoringDifferential:
            typeof info?.scoring_differential === "number"
              ? info.scoring_differential.toFixed(2)
              : info?.scoring_differential || "N/A",
          isLinked: has,
        });

        setAuthState(prev => (prev ? { ...prev, hasJgsMapping: has } : prev));

        try {
          sessionStorage.setItem("recruit_ai_has_jgs", String(has));
        } catch {}
      }
    },
    [updatePlayerSummary]
  );

  // ─── Re-fetch /users/me and update context (e.g. after JGS link) ───
  const refreshUserData = useCallback(async () => {
    try {
      const me = await fetchCurrentUser();
      setCurrentUser(me);
      setDismissedNextSteps((me as any)?.profile?.dismissedNextSteps ?? []);
      extractSummaryFromUser(me);
    } catch (err) {
      console.warn("[EndUserLayout] refreshUserData error", err);
    }
  }, [extractSummaryFromUser]);

  // ─── Close sidebar on Escape ───
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsSidebarOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // ─── Auth init: single /users/me call — cookie is the source of truth ───
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        // Development override
        if (DEV_MODE && import.meta.env.VITE_DEV_TOKEN) {
          const devUserId = import.meta.env.VITE_DEV_USER_ID || "dev-user";
          if (!cancelled) {
            setAuthState({ token: null, userId: devUserId, hasJgsMapping: false });
          }
          toast.success("Development mode loaded");
          return;
        }

        // Single /users/me call — if the cookie is valid, we get the user back
        const me = await fetchCurrentUser();
        if (cancelled) return;

        const userId = me?.user_id || me?._id || me?.id;
        if (!userId) {
          setAuthError("Please sign in to continue.");
          return;
        }

        try {
          sessionStorage.setItem("recruit_ai_analytics_user_id", String(userId));
        } catch {}
        analytics.identify({
          userId: String(userId),
          email: typeof me?.email === "string" ? me.email : undefined,
          userType: me?.isEval ? "eval" : "end_user",
        });

        setAuthState({
          token: null,
          userId: String(userId),
          hasJgsMapping: !!(me as any)?.has_jgs_mapping || !!(me as any)?.hasJgsMapping,
        });
        setCurrentUser(me);
        setDismissedNextSteps((me as any)?.profile?.dismissedNextSteps ?? []);
        extractSummaryFromUser(me);
      } catch (error) {
        console.error("[EndUserLayout] Auth init failed:", error);
        if (!cancelled) {
          setAuthError("Please sign in to continue.");
        }
      } finally {
        if (!cancelled) setIsAuthenticating(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reinitKey]);

  // ─── Check subscription status for Individual Signup users ───
  useEffect(() => {
    if (isAuthenticating || !authState?.userId) return;
    getSubscription()
      .then(sub => {
        if (!sub?.subscriptionState) return; // Not an individual signup user (endpoint fails)
        setSubscriptionState(sub.subscriptionState);
        if (sub.currentPeriodEnd) setSubscriptionEndDate(sub.currentPeriodEnd);
      })
      .catch(() => {
        // Non-individual users or unauthenticated — silently ignore
      });
  }, [isAuthenticating, authState?.userId]);

  // ─── Global 402 interceptor for subscription gating ───
  useEffect(() => {
    if (isAuthenticating || !authState?.userId) return;

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (response.status === 402) {
        try {
          const cloned = response.clone();
          const body = await cloned.json();
          if (body?.error === "subscription_required" && body?.subscriptionState) {
            setSubscriptionState(body.subscriptionState);
          }
        } catch {
          // Could not parse — ignore
        }
      }
      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [isAuthenticating, authState?.userId]);

  // ─── Fetch chat history once authenticated ───
  useEffect(() => {
    if (isAuthenticating || !authState?.userId) return;
    fetchChatThreads(1, 20)
      .then((threads: ThreadListItem[]) => {
        if (Array.isArray(threads)) {
          setChatHistory(
            threads.map(t => ({
              id: t.threadId,
              title: t.title || "New Chat",
              timestamp: new Date(t.lastUpdated || Date.now()).toLocaleString(),
              messageCount: t.messageCount,
            }))
          );
        }
      })
      .catch(() => {});
  }, [isAuthenticating, authState?.userId]);

  // ─── Dismiss next step ───
  const dismissNextStep = useCallback(
    async (key: string) => {
      const updated = [...dismissedNextSteps, key];
      setDismissedNextSteps(updated);
      try {
        await updateProfileField("dismissedNextSteps", updated);
      } catch {
        setDismissedNextSteps(dismissedNextSteps);
      }
    },
    [dismissedNextSteps]
  );

  // ─── Reset dismissed next steps ───
  const resetDismissedNextSteps = useCallback(async () => {
    setDismissedNextSteps([]);
    try {
      await updateProfileField("dismissedNextSteps", []);
    } catch {
      // silently ignore — state already cleared locally
    }
  }, []);

  // ─── Logout ───
  const handleLogout = useCallback(async () => {
    try {
      trackLogout();
      try {
        await logoutAll();
      } catch {}
      // Reset context state
      setAuthState(null);
      setAuthError("logged out");
      setPlayerSummary(defaultSummary);
      setChatHistory([]);
      setUserType("player");
      setIsSidebarOpen(false);
      setHeaderCallbacks({});
      setCurrentUser(null);
      setSubscriptionState(null);
      setSubscriptionEndDate(null);
      clearNextStepCache();
    } catch {
      setAuthState(null);
      setAuthError("logged out");
    }
  }, []);

  // ─── Login success callback ───
  const handleLoginSuccess = useCallback(() => {
    setAuthError(null);
    setIsAuthenticating(true);
    setReinitKey(k => k + 1);
  }, []);

  // ─── Context value ───
  const contextValue = useMemo<EndUserContextValue>(
    () => ({
      authState,
      setAuthState,
      playerSummary,
      updatePlayerSummary,
      userType,
      setUserType,
      isSidebarOpen,
      openSidebar,
      closeSidebar,
      chatHistory,
      setChatHistory,
      handleLogout,
      onShare: headerCallbacks.onShare,
      setOnShare,
      currentUser,
      refreshUserData,
      subscriptionState,
      subscriptionEndDate,
      setSubscriptionGate,
      dismissedNextSteps,
      dismissNextStep,
      resetDismissedNextSteps,
    }),
    [
      authState,
      playerSummary,
      userType,
      isSidebarOpen,
      chatHistory,
      handleLogout,
      headerCallbacks,
      currentUser,
      openSidebar,
      closeSidebar,
      updatePlayerSummary,
      setOnShare,
      refreshUserData,
      subscriptionState,
      subscriptionEndDate,
      setSubscriptionGate,
      dismissedNextSteps,
      dismissNextStep,
      resetDismissedNextSteps,
    ]
  );

  // ─── Loading state ───
  if (isAuthenticating) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="text-center">
          <div className="mb-4">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
          <p className="text-base text-muted-foreground">Authenticating...</p>
        </div>
      </div>
    );
  }

  // ─── Not authenticated — show login ───
  if (authError) {
    return <RootLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // ─── Determine subscription gate state ───
  const blockedStates = ["expired", "canceled", "past_due", "none"];
  const isImpersonating = sessionStorage.getItem("recruit_ai_is_impersonated") === "1";
  const isSubscriptionBlocked =
    subscriptionState !== null && blockedStates.includes(subscriptionState) && !isImpersonating;
  const isSubscriptionCanceling = subscriptionState === "canceling";
  const showImpersonationSubscriptionBanner =
    isImpersonating && subscriptionState !== null && blockedStates.includes(subscriptionState);

  // ─── Authenticated — persistent header + page outlet ───
  return (
    <EndUserContext.Provider value={contextValue}>
      <AppLayout userType={userType}>
        <DevModeIndicator />
        {isSubscriptionCanceling && <SubscriptionBanner currentPeriodEnd={subscriptionEndDate} />}
        {isSubscriptionBlocked && (
          <SubscriptionGateOverlay
            subscriptionState={subscriptionState as BlockedSubscriptionState}
          />
        )}
        {showImpersonationSubscriptionBanner && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-800 text-center">
            This user does not have an active subscription ({subscriptionState}). You are viewing as
            an admin.
          </div>
        )}
        <ChatHeader
          playerName={playerSummary.playerName}
          jgsRank={playerSummary.jgsRank}
          class_rank={playerSummary.classRank}
          class_year={playerSummary.classYear}
          profileGradYear={playerSummary.profileGradYear}
          scoringDifferential={playerSummary.scoringDifferential}
          isLinked={playerSummary.isLinked}
          onOpenSidebar={openSidebar}
          onShare={headerCallbacks.onShare}
          onLogout={handleLogout}
          userType={userType}
          onLinkProfile={() => navigate("/profile")}
        />
        <OnboardingModal
          isLinked={playerSummary.isLinked}
          userType={userType}
          onLinkSuccess={refreshUserData}
          onboardingCompletedAt={(currentUser as any)?.onboardingCompletedAt}
          onboardingVersion={(currentUser as any)?.onboardingVersion}
          onboardingProgress={(currentUser as any)?.onboardingProgress}
          isReturningUser={!!(currentUser as any)?.isReturningUser}
          onLogout={handleLogout}
          profile={buildOnboardingProfile(
            (currentUser as any)?.profile,
            (currentUser as any)?.jgs_student_data,
            playerSummary.isLinked
          )}
        />
        <Outlet />
      </AppLayout>
    </EndUserContext.Provider>
  );
};

export default EndUserLayout;
